import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import apiService from '../services/api';
import { isHandlerSite } from '../config/siteMode';
import { 
  Box, 
  CircularProgress, 
  Container, 
  Typography, 
  Card, 
  CardContent, 
  Button, 
  Grid,
  Avatar,
  Chip
} from '@mui/material';
import { Pets, DirectionsWalk } from '@mui/icons-material';

const Dashboard = () => {
  const navigate = useNavigate();
  const { user, isAuthenticated } = useAuth();
  const [loading, setLoading] = useState(true);
  const [applicationStatus, setApplicationStatus] = useState(null);

  useEffect(() => {
    const checkUserRoles = async () => {
      if (!isAuthenticated) {
        navigate('/auth');
        return;
      }

      // Handler site: approved handlers go to their dashboard; everyone else
      // goes to the application flow. (No owner/admin routes exist here.)
      if (isHandlerSite) {
        navigate(user?.is_handler ? '/handler/dashboard' : '/handler/apply');
        return;
      }

      // Staff have no owner/handler features — send them to the admin portal.
      if (user?.is_staff) {
        navigate('/admin/dashboard');
        return;
      }

      // Check for handler application if user is not a handler
      if (user?.is_owner && !user?.is_handler) {
        try {
          const appStatus = await apiService.getHandlerApplicationStatus();
          setApplicationStatus(appStatus);
          
          // If application is approved, allow access to handler dashboard
          if (appStatus.has_application && appStatus.status === 'approved') {
            // User can access both dashboards
            setLoading(false);
            return;
          }
        } catch (err) {
          console.error('Error checking application status:', err);
          // If there's an authentication error, redirect to auth
          if (err.message === 'Authentication required' || err.message.includes('401')) {
            navigate('/auth');
            return;
          }
        }
      }

      // If user has only one role, redirect immediately
      if (user?.is_handler && !user?.is_owner) {
        navigate('/handler/dashboard');
        return;
      } else if (user?.is_owner && !user?.is_handler) {
        // Check if they have an approved application (already handled above)
        navigate('/owner/dashboard');
        return;
      } else if (!user?.is_handler && !user?.is_owner) {
        // Default to owner dashboard if no specific role is set
        navigate('/owner/dashboard');
        return;
      }

      // If user has both roles or approved application, show selection screen
      setLoading(false);
    };

    checkUserRoles();
  }, [isAuthenticated, user, navigate]);

  const handleDashboardSelection = (dashboardType) => {
    if (dashboardType === 'owner') {
      navigate('/owner/dashboard');
    } else if (dashboardType === 'handler') {
      navigate('/handler/dashboard');
    }
  };

  // Show loading while checking user roles
  if (loading) {
  return (
    <Container maxWidth="lg" sx={{ py: 4 }}>
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
        <CircularProgress size={60} />
        </Box>
      </Container>
    );
  }

  // Show dashboard selection for users with both roles or approved applications
  const hasHandlerAccess = user?.is_handler || (applicationStatus?.has_application && applicationStatus?.status === 'approved');
  
  return (
    <Container maxWidth="md" sx={{ py: 4 }}>
      <Box sx={{ textAlign: 'center', mb: 4 }}>
        <Typography variant="h3" component="h1" sx={{ fontWeight: 700, mb: 2 }}>
          Welcome back, {user?.first_name}!
        </Typography>
        <Typography variant="h6" color="text.secondary" sx={{ mb: 4 }}>
          {hasHandlerAccess 
            ? 'You have access to both owner and handler features. Choose your dashboard:'
            : 'Choose your dashboard:'
          }
        </Typography>
      </Box>

      <Grid container spacing={3} sx={{ justifyContent: hasHandlerAccess ? 'center' : 'center' }}>
        <Grid item xs={12} md={hasHandlerAccess ? 6 : 8}>
          <Card 
            sx={{ 
              p: 3, 
              textAlign: 'center', 
              cursor: 'pointer',
              transition: 'transform 0.2s, box-shadow 0.2s',
              '&:hover': {
                transform: 'translateY(-4px)',
                boxShadow: 4
              }
            }}
            onClick={() => handleDashboardSelection('owner')}
          >
            <CardContent>
              <Avatar 
                sx={{ 
                  bgcolor: 'primary.main', 
                  width: 80, 
                  height: 80, 
                  mx: 'auto', 
                  mb: 2 
                }}
              >
                <Pets sx={{ fontSize: 40 }} />
              </Avatar>
              <Typography variant="h5" sx={{ fontWeight: 600, mb: 2 }}>
                Pet Owner Dashboard
              </Typography>
              <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
                Manage your pets, book services, track ongoing services, and review handler services.
              </Typography>
              <Chip 
                label="Pet Owner" 
                color="primary" 
                sx={{ mb: 2 }}
              />
              <Button 
                variant="contained" 
                fullWidth
                size="large"
                onClick={(e) => {
                  e.stopPropagation();
                  handleDashboardSelection('owner');
                }}
              >
                Go to Owner Dashboard
              </Button>
            </CardContent>
          </Card>
        </Grid>

        {hasHandlerAccess && (
          <Grid item xs={12} md={6}>
          <Card 
            sx={{ 
              p: 3, 
              textAlign: 'center', 
              cursor: 'pointer',
              transition: 'transform 0.2s, box-shadow 0.2s',
              '&:hover': {
                transform: 'translateY(-4px)',
                boxShadow: 4
              }
            }}
            onClick={() => handleDashboardSelection('handler')}
          >
            <CardContent>
              <Avatar 
                sx={{ 
                  bgcolor: 'secondary.main', 
                  width: 80, 
                  height: 80, 
                  mx: 'auto', 
                  mb: 2 
                }}
              >
                <DirectionsWalk sx={{ fontSize: 40 }} />
              </Avatar>
              <Typography variant="h5" sx={{ fontWeight: 600, mb: 2 }}>
                Handler Dashboard
              </Typography>
              <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
                Accept service requests, manage active services, send updates to owners, and track earnings.
              </Typography>
              <Chip 
                label="Pet Handler" 
                color="secondary" 
                sx={{ mb: 2 }}
              />
              <Button 
                variant="contained" 
                color="secondary"
                fullWidth
                size="large"
                onClick={(e) => {
                  e.stopPropagation();
                  handleDashboardSelection('handler');
                }}
              >
                Go to Handler Dashboard
              </Button>
            </CardContent>
          </Card>
        </Grid>
        )}
      </Grid>

      <Box sx={{ textAlign: 'center', mt: 4 }}>
        <Typography variant="body2" color="text.secondary">
          You can switch between dashboards anytime using the navigation menu
        </Typography>
      </Box>
    </Container>
  );
};

export default Dashboard; 